import { useState, useEffect, useRef } from 'react'

interface UseRecaptchaOptions {
  action: string
  onTokenReceived?: (token: string) => void
  onError?: (error: string) => void
}

interface UseRecaptchaReturn {
  recaptchaReady: boolean
  captchaError: string | null
  executeRecaptcha: () => Promise<string | null>
  isExecuting: boolean
}

export const useRecaptcha = ({ action, onTokenReceived, onError }: UseRecaptchaOptions): UseRecaptchaReturn => {
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const [captchaError, setCaptchaError] = useState<string | null>(null)
  const [isExecuting, setIsExecuting] = useState(false)
  const recaptchaWidgetId = useRef<number | null>(null)

  // Load Google reCAPTCHA Enterprise script in head
  useEffect(() => {
    const siteKey = import.meta.env.RECAPTCHA_SITE_KEY
    console.log('🔑 reCAPTCHA Site Key loaded:', siteKey ? 'YES' : 'NO')
    if (!siteKey) {
      console.error('❌ RECAPTCHA_SITE_KEY not found in environment variables')
      setCaptchaError('Captcha configuration error. Please try again later.')
      return
    }

    // Remove any previously rendered reCAPTCHA widgets (from HMR/old code)
    const oldWidgets = document.querySelectorAll('.g-recaptcha')
    oldWidgets.forEach((node) => {
      try { node.parentElement?.removeChild(node) } catch {}
    })

    const existingScript = document.getElementById('recaptcha-script') as HTMLScriptElement | null
    if (!existingScript) {
      console.log('📜 Loading reCAPTCHA Enterprise script in head...')
      const script = document.createElement('script')
      script.id = 'recaptcha-script'
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`
      script.async = true
      script.defer = true
      script.onload = () => {
        console.log('✅ reCAPTCHA Enterprise script loaded successfully')
        // Wait a bit for the script to fully initialize
        setTimeout(() => {
          setRecaptchaReady(true)
        }, 100)
      }
      script.onerror = () => {
        console.error('❌ Failed to load reCAPTCHA Enterprise script')
        setCaptchaError('Failed to load captcha. Please refresh the page and try again.')
      }
      // Add to head instead of body
      document.head.appendChild(script)
    } else {
      console.log('📜 reCAPTCHA script already exists')
      setRecaptchaReady(true)
    }
  }, [])

  // Initialize reCAPTCHA v3 when script is ready
  useEffect(() => {
    const siteKey = import.meta.env.RECAPTCHA_SITE_KEY
    if (!recaptchaReady || !siteKey) return
    
    // Wait for grecaptcha to be available
    const checkGrecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        console.log('🚀 reCAPTCHA Enterprise ready, grecaptcha available:', !!window.grecaptcha)
        console.log('🔧 grecaptcha methods:', Object.keys(window.grecaptcha))
        console.log('🔧 grecaptcha.enterprise available:', !!window.grecaptcha?.enterprise)
      } else {
        console.log('⏳ Waiting for grecaptcha to load...')
        setTimeout(checkGrecaptcha, 100)
      }
    }
    
    checkGrecaptcha()
  }, [recaptchaReady])

  const executeRecaptcha = async (): Promise<string | null> => {
    const siteKey = import.meta.env.RECAPTCHA_SITE_KEY
    console.log('🔄 Executing reCAPTCHA v3 with action:', action)
    
    if (!siteKey) {
      console.error('❌ RECAPTCHA_SITE_KEY not found')
      const error = 'Captcha configuration error. Please try again later.'
      setCaptchaError(error)
      onError?.(error)
      return null
    }

    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
      console.error('❌ grecaptcha not available')
      const error = 'Captcha not loaded. Please refresh the page and try again.'
      setCaptchaError(error)
      onError?.(error)
      return null
    }

    setIsExecuting(true)
    setCaptchaError(null)

    try {
      // Safety timeout: if Google never calls our callback, fail gracefully
      const guard = setTimeout(() => {
        if (isExecuting) {
          console.log('⏰ reCAPTCHA timeout after 10 seconds')
          setIsExecuting(false)
          const error = 'Captcha timeout. Please try again.'
          setCaptchaError(error)
          onError?.(error)
        }
      }, 10000)

      // Execute reCAPTCHA Enterprise v3 (following Google docs exactly)
      return new Promise((resolve, reject) => {
        window.grecaptcha.enterprise.ready(async () => {
          console.log('🎯 reCAPTCHA Enterprise ready callback triggered')
          try {
            const token = await window.grecaptcha.enterprise.execute(siteKey, { action })
            console.log('🎫 reCAPTCHA Enterprise token received:', token ? 'YES' : 'NO')
            clearTimeout(guard)
            setIsExecuting(false)
            
            if (token) {
              onTokenReceived?.(token)
              resolve(token)
            } else {
              console.error('❌ reCAPTCHA returned empty token')
              const error = 'Captcha failed. Please try again.'
              setCaptchaError(error)
              onError?.(error)
              resolve(null)
            }
          } catch (error) {
            console.error('❌ reCAPTCHA execute error:', error)
            clearTimeout(guard)
            setIsExecuting(false)
            const errorMsg = 'Captcha failed to execute. Please try again.'
            setCaptchaError(errorMsg)
            onError?.(errorMsg)
            resolve(null)
          }
        })
      })
    } catch (err) {
      console.error('❌ reCAPTCHA setup error:', err)
      setIsExecuting(false)
      const error = 'Captcha error. Please try again later.'
      setCaptchaError(error)
      onError?.(error)
      return null
    }
  }

  return {
    recaptchaReady,
    captchaError,
    executeRecaptcha,
    isExecuting
  }
}

// Extend Window interface to include grecaptcha
declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void
        execute: (siteKey: string, options: { action: string }) => Promise<string>
      }
    }
  }
}
